import { BackArrow } from "@/components/icons/BackArrow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import { useConfirm } from "@/components/DialogProvider";
import { useAuth } from "@/contexts/AuthContext";
import { ProjectProvider } from "@/contexts/ProjectContext";
import { useAgencies } from "@/features/agencies/hooks/useAgencies";
import { useProjectStatuses } from "@/features/config/hooks/useConfig";
import { useProjectNotes } from "@/features/projects/hooks/useProjectNotes";
import { useProject, useProjects } from "@/features/projects/hooks/useProjects";
import * as projectService from "@/features/projects/api/projectService";
import { useTasks } from "@/features/tasks/hooks/useTasks";
import { useUsers } from "@/features/users/hooks/useUsers";
import { getProjectWorkerAssignments } from "@/lib/projectAssignmentHelpers";
import { getProjectStatusAlerts } from "@/lib/projectStatusAlerts";
import { formatDateTimeShort, isDateBeforeTodayEST } from "@/lib/utils";
import { createFileRoute, Link, Outlet, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ProjectEditProvider } from "./-ProjectEditContext";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId")({
  component: RouteComponent,
});

function EditButton() {
  const location = useLocation();
  const { projectId } = Route.useParams();
  const navigate = Route.useNavigate();

  // Only show on the index/general tab
  const isGeneralTab =
    location.pathname === `/dashboard/admin/projects/${projectId}/` ||
    location.pathname === `/dashboard/admin/projects/${projectId}`;

  if (!isGeneralTab) {
    return null;
  }

  const handleEditClick = () => {
    navigate({
      to: "/dashboard/admin/projects/$projectId",
      params: { projectId },
      search: { edit: true },
    });
  };

  return (
    <Button variant="primary" className="w-full min-[744px]:w-auto" onClick={handleEditClick}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.5566 1.94368C11.7516 1.74865 12.0185 1.63867 12.2972 1.63867C12.5758 1.63867 12.8427 1.74865 13.0377 1.94368C13.2328 2.1387 13.3427 2.40559 13.3427 2.68424C13.3427 2.96289 13.2328 3.22978 13.0377 3.4248L4.31021 12.1523L1.63867 12.8895L2.37585 10.218L11.5566 1.94368Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Edit
    </Button>
  );
}

function DeleteProjectButton() {
  const location = useLocation();
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [isDeleting, setIsDeleting] = useState(false);

  const isGeneralTab =
    location.pathname === `/dashboard/admin/projects/${projectId}/` ||
    location.pathname === `/dashboard/admin/projects/${projectId}`;

  if (!isGeneralTab) {
    return null;
  }

  const handleDeleteClick = async () => {
    const confirmed = await confirm({
      title: "Move to Trash?",
      description:
        "This project will be moved to the trash. You can restore it later from the Trash section.",
      confirmText: "Move to Trash",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await projectService.softDeleteProject(projectId);
      toast.success("Project moved to trash");
      navigate({ to: "/dashboard/admin/projects" });
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error("Failed to move project to trash. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="w-full min-[744px]:w-auto"
      onClick={handleDeleteClick}
      disabled={isDeleting}
    >
      <Trash2 className="h-4 w-4" />
      {isDeleting ? "Moving..." : "Move to Trash"}
    </Button>
  );
}

function RouteComponent() {
  const { projectId } = Route.useParams();
  const location = useLocation();
  const { project, loading: projectLoading } = useProject(projectId);
  const { updateProject } = useProjects();
  const { users } = useUsers();
  const { agencies } = useAgencies();
  const { projectStatuses } = useProjectStatuses();
  const { userProfile } = useAuth();
  const { notes, unreadCount } = useProjectNotes(projectId, userProfile?.uid);
  const { tasks } = useTasks({ projectId });
  const [alertExpanded, setAlertExpanded] = useState(false);
  const navigate = Route.useNavigate();
  const router = useRouter();

  // Calculate total and overdue task counts (using EST timezone)
  const { totalTasks, overdueTasks } = useMemo(() => {
    const overdue = tasks.filter((task) => {
      if (!task.dueDate || task.status === "done") return false;
      return isDateBeforeTodayEST(task.dueDate);
    });

    return {
      totalTasks: tasks.length,
      overdueTasks: overdue.length,
    };
  }, [tasks]);

  if (projectLoading || !project) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Get related data
  const customer = users.find((u) => u.uid === project.customerId);

  // Get all assigned managers
  const managerIds = project.assignedManagerIds || [];
  const managers = users.filter((u) => managerIds.includes(u.uid));
  const agency = agencies.find((a) => a.id === project.assignedAgencyId);
  const status = project.statusId ? projectStatuses.find((s) => s.id === project.statusId) : null;

  // Calculate worker assignments
  const workerAssignments = getProjectWorkerAssignments(project);
  const activeWorkerAssignments = workerAssignments.filter((assignment) => !assignment.inactive);
  const totalWorkersNeeded = (project.workUnits || []).reduce(
    (sum, wu) => sum + (wu.workersNeeded || 0),
    0,
  );
  const workersDisplay = `${activeWorkerAssignments.length}/${totalWorkersNeeded}`;

  // Get status alerts
  const projectAlert = getProjectStatusAlerts([project], projectStatuses)[0] || null;

  // Format dates
  const startDate = project.startDate ? formatDateTimeShort(project.startDate) : "-";
  const endDate = project.endDate ? formatDateTimeShort(project.endDate) : "-";

  // Handle status change
  const handleStatusChange = async (statusId: string) => {
    try {
      await updateProject(projectId, { statusId });
      toast.success("Project status updated successfully");
    } catch (error) {
      console.error("Failed to update project status:", error);
      toast.error("Failed to update project status");
    }
  };

  // Determine active tab from location
  const isGeneralTab =
    location.pathname === `/dashboard/admin/projects/${projectId}/` ||
    location.pathname === `/dashboard/admin/projects/${projectId}`;
  const isScheduleTab = location.pathname.endsWith("/schedule");
  const isTimelineTab = location.pathname.endsWith("/timeline");
  const isAuditLogsTab = location.pathname.endsWith("/audit_logs");
  const isHoursTab = location.pathname.endsWith("/hours");
  const isNotesTab = location.pathname.endsWith("/notes");
  const isTasksTab = location.pathname.endsWith("/tasks");

  return (
    <ProjectEditProvider>
      <ProjectProvider value={{ project, loading: projectLoading }}>
        <div className="flex min-w-0 flex-col bg-[#F1F5F9]">
          {/* Back button and Edit button */}
          <div className="mb-4 flex flex-col gap-2 min-[744px]:flex-row min-[744px]:items-center min-[744px]:justify-between">
            <button
              onClick={() => router.history.back()}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <BackArrow className="h-6 w-6" />
              <span className="text-2xl font-bold text-gray-900">Project details</span>
            </button>
            <div className="flex w-full gap-2 min-[744px]:w-auto">
              <DeleteProjectButton />
              <EditButton />
            </div>
          </div>
          {/* Project Header Info Card */}
          <div className="mb-2 rounded-lg border border-gray-200 bg-white p-2">
            <div className="mb-2 flex flex-col gap-2 min-[744px]:flex-row min-[744px]:items-start min-[744px]:justify-between">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-semibold text-gray-900">{project.name}</h1>
                  {status && (
                    <Badge
                      style={{ backgroundColor: status.color || "#6b7280", color: "white" }}
                      className="border-0"
                    >
                      {status.name}
                    </Badge>
                  )}
                  <span className="text-sm text-gray-600">
                    {project.type === "one-time" ? "One-time" : "Ongoing"}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center min-[744px]:w-auto">
                <SearchableSelectPopover
                  items={projectStatuses}
                  selectedValue={project.statusId || null}
                  onSelect={(value) => {
                    if (value) {
                      handleStatusChange(value);
                    }
                  }}
                  getKey={(status) => status.id}
                  getLabel={(status) => status.name}
                  triggerLabel="Change status"
                  placeholder="Search status..."
                  emptyText="No status found"
                  buttonClassName="w-full justify-between min-[744px]:w-auto min-[744px]:justify-center"
                  contentClassName="w-full p-0"
                />
              </div>
            </div>

            <div className="mt-4 divide-y divide-gray-200 min-[744px]:hidden">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">Customer</span>
                {customer ? (
                  <Link
                    to="/dashboard/admin/customers/$customerId"
                    params={{ customerId: customer.uid }}
                    className="text-sm font-medium text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                  >
                    {customer.name}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-gray-900">No customer assigned</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">
                  Manager{managers.length > 1 ? "s" : ""}
                </span>
                {managers.length > 0 ? (
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {managers.map((mgr, idx) => (
                      <span key={mgr.uid} className="text-sm">
                        <Link
                          to="/dashboard/admin/users"
                          className="font-medium text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                        >
                          {mgr.name}
                        </Link>
                        {idx < managers.length - 1 && <span className="text-gray-500">, </span>}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm font-medium text-gray-900">No manager assigned</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">Agency</span>
                {agency ? (
                  <Link
                    to="/dashboard/admin/agencies/$agencyId"
                    params={{ agencyId: agency.id }}
                    className="text-sm font-medium text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                  >
                    {agency.name}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-gray-900">No agency assigned</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">Workers</span>
                <span className="text-sm font-medium text-gray-900">{workersDisplay}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">Start Date</span>
                <span className="text-sm font-medium text-gray-900">{startDate}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-3">
                <span className="text-muted-foreground text-sm">End Date</span>
                <span className="text-sm font-medium text-gray-900">{endDate}</span>
              </div>
            </div>

            <div className="mt-4 hidden min-[744px]:block">
              <div className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex min-w-max flex-nowrap items-start divide-x divide-gray-200">
                  <div className="shrink-0 pr-6">
                    <div className="text-sm text-gray-500">Customer</div>
                    {customer ? (
                      <Link
                        to="/dashboard/admin/customers/$customerId"
                        params={{ customerId: customer.uid }}
                        className="block text-sm font-medium whitespace-nowrap text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                      >
                        {customer.name}
                      </Link>
                    ) : (
                      <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                        No customer assigned
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 px-6">
                    <div className="text-sm text-gray-500">
                      Manager{managers.length > 1 ? "s" : ""}
                    </div>
                    {managers.length > 0 ? (
                      <div className="space-y-1">
                        {managers.map((mgr) => (
                          <Link
                            key={mgr.uid}
                            to="/dashboard/admin/users"
                            className="block text-sm font-medium whitespace-nowrap text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                          >
                            {mgr.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                        No manager assigned
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 px-6">
                    <div className="text-sm text-gray-500">Agency</div>
                    {agency ? (
                      <Link
                        to="/dashboard/admin/agencies/$agencyId"
                        params={{ agencyId: agency.id }}
                        className="block text-sm font-medium whitespace-nowrap text-gray-900 underline decoration-transparent transition-colors hover:decoration-gray-900"
                      >
                        {agency.name}
                      </Link>
                    ) : (
                      <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                        No agency assigned
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 px-6">
                    <div className="text-sm text-gray-500">Workers</div>
                    <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                      {workersDisplay}
                    </div>
                  </div>
                  <div className="shrink-0 px-6">
                    <div className="text-sm text-gray-500">Start Date</div>
                    <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                      {startDate}
                    </div>
                  </div>
                  <div className="shrink-0 pl-6">
                    <div className="text-sm text-gray-500">End Date</div>
                    <div className="text-sm font-medium whitespace-nowrap text-gray-900">
                      {endDate}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Alert Banner Accordion */}
            {projectAlert && (
              <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50">
                <button
                  onClick={() => setAlertExpanded(!alertExpanded)}
                  className="flex w-full items-center justify-between px-2 py-1.5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-md border border-orange-500 px-2.5 py-1.5">
                      <svg
                        className="h-4 w-4 text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-orange-500">Alert</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      Project exceeds threshold
                    </span>
                    <span className="text-sm text-gray-500">
                      {projectAlert.daysOverThreshold} day
                      {projectAlert.daysOverThreshold !== 1 ? "s" : ""} over threshold
                    </span>
                  </div>
                  {alertExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {alertExpanded && (
                  <div className="border-t border-orange-200 px-4 py-3">
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">
                      {projectAlert.daysOverThreshold} day
                      {projectAlert.daysOverThreshold !== 1 ? "s" : ""} over threshold
                    </h4>
                    <p className="text-sm text-gray-700">
                      This project has been in <strong>{projectAlert.statusName}</strong> status for{" "}
                      <strong>{projectAlert.daysSinceChange} days</strong> (threshold:{" "}
                      {projectAlert.threshold} days). Consider updating the project status or taking
                      necessary action.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Tabs */}
          <div className="mb-0">
            <div className="flex gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:gap-2 [&::-webkit-scrollbar]:hidden">
              <button
                onClick={() =>
                  navigate({
                    to: `/dashboard/admin/projects/${projectId}/`,
                  })
                }
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isGeneralTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                General
              </button>
              <button
                onClick={() => navigate({ to: `/dashboard/admin/projects/${projectId}/schedule` })}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isScheduleTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                Schedule
              </button>
              <button
                onClick={() => navigate({ to: `/dashboard/admin/projects/${projectId}/hours` })}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isHoursTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                Project Hours
              </button>
              <button
                onClick={() => navigate({ to: `/dashboard/admin/projects/${projectId}/timeline` })}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isTimelineTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() =>
                  navigate({ to: `/dashboard/admin/projects/${projectId}/audit_logs` })
                }
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isAuditLogsTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                Audit logs
              </button>
              <button
                onClick={() => navigate({ to: `/dashboard/admin/projects/${projectId}/notes` })}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isNotesTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  Notes
                  {notes.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="scale-[0.85] bg-gray-500 px-1.5 py-0 text-xs text-white hover:bg-gray-600"
                    >
                      {notes.length}
                    </Badge>
                  )}
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="scale-[0.85] px-1.5 py-0 text-xs">
                      {unreadCount}
                    </Badge>
                  )}
                </span>
              </button>
              <button
                onClick={() => navigate({ to: `/dashboard/admin/projects/${projectId}/tasks` })}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isTasksTab
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  Tasks
                  {totalTasks > 0 && (
                    <Badge
                      variant="secondary"
                      className="scale-[0.85] bg-gray-500 px-1.5 py-0 text-xs text-white hover:bg-gray-600"
                    >
                      {totalTasks}
                    </Badge>
                  )}
                  {overdueTasks > 0 && (
                    <Badge variant="destructive" className="scale-[0.85] px-1.5 py-0 text-xs">
                      {overdueTasks}
                    </Badge>
                  )}
                </span>
              </button>
            </div>
          </div>
          {/* Tab Content */}
          <div className="mt-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </ProjectProvider>
    </ProjectEditProvider>
  );
}
